import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ToastrService } from './toastr.service';
import { Router } from '@angular/router';
import { EntityService } from '../data/entity.service';
export interface ParamsObject {
  [param: string]: string | string[];
}

@Injectable()
export class ApiService extends EntityService {

  private subject: BehaviorSubject<any>[] = [];

  constructor(
    private http: HttpClient,
    private toaster: ToastrService,
    private router: Router,
  ) {
    super();
  }

  delete(path, body= {}, keyWordSubjectToRefresh?: string[]): Observable<any> {
    this.reinitialiseSubjectsByKeyword(keyWordSubjectToRefresh);
    return this.http.request(
      'delete',
      `${environment.apiUrl}${path}`,
      {
        body,
        ...this.makeHttpOptions(null, ''),
      },
    ).pipe(
      catchError((error) => this.handleErrors(error)),
    );
  }

  download(path: string, params: HttpParams = new HttpParams()): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}${path}`,
      {
        ...this.makeHttpOptions(params, ''),
        responseType: 'blob',
      },
    ).pipe(
      catchError((error) => this.handleErrors(error)),
    );
  }

  get(path: string, params: HttpParams = new HttpParams(), lang: string = ''): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}${path}`,
      this.makeHttpOptions(params, lang),
    ).pipe(
      catchError((error) => this.handleErrors(error)),
    );
  }

  public getSave(path: string, args?: any): Observable<any> {
    const params = args?.find(arg => arg instanceof HttpParams) ?? new HttpParams();
    const lang = args?.find(arg => typeof arg === 'string') ?? '';
    const parameters = [this.http, `${environment.apiUrl}${path}`, this.makeHttpOptions, params, lang];
    return this.getSavedApiCall(this.subject, this.getApi, parameters);
  }

  /**
   * Deducts more precisely by reading error message if the issue is due
   * to an unknown member needing to be registered fully in the system
   *
   * @param err
   *
   * @return
   */
  public internalSignUpNeeded(err: any): any {
    const errorMessage = err.error.error || err.error.message;
    return err.status === 401 &&
      (errorMessage.includes('Unable to check the token')
        || errorMessage.includes('Internal member registration needed')
      );
  }

  patch(path: string, body= {}, lang: string = '', keyWordSubjectToRefresh?: string[]): Observable<any> {
    this.reinitialiseSubjectsByKeyword(keyWordSubjectToRefresh);
    return this.http.patch(
      `${environment.apiUrl}${path}`,
      JSON.stringify(body),
      this.makeHttpOptions(null, lang),
    ).pipe(
      catchError((error) => this.handleErrors(error)),
    );
  }

  post(path: string, body= {}, lang: string = '', keyWordSubjectToRefresh?: string[]): Observable<any> {
    this.reinitialiseSubjectsByKeyword(keyWordSubjectToRefresh);
    return this.http.post(
      `${environment.apiUrl}${path}`,
      JSON.stringify(body),
      this.makeHttpOptions(null, lang),
    ).pipe(
      catchError((error) => this.handleErrors(error)),
    );
  }

  put(path: string, body= {}, lang: string = '', keyWordSubjectToRefresh?: string[]): Observable<any> {
    this.reinitialiseSubjectsByKeyword(keyWordSubjectToRefresh);
    return this.http.put(
      `${environment.apiUrl}${path}`,
      JSON.stringify(body),
      this.makeHttpOptions(null, lang),
    ).pipe(
      catchError((error) => this.handleErrors(error)),
    );
  }

  upload(path: string, body= {}, params: HttpParams = new HttpParams(), keyWordSubjectToRefresh?: string[]):
    Observable<any> {
    this.reinitialiseSubjectsByKeyword(keyWordSubjectToRefresh);
    return this.http.post(
      `${environment.apiUrl}${path}`,
      body,
      {
        ...this.makeHttpOptions(params, ''),
        reportProgress: true,
      },
    ).pipe(
      catchError((error) => this.handleErrors(error)),
    );
  }

  private getApi(
    service: HttpClient,
    path: string,
    makerOption: any,
    params: HttpParams = new HttpParams(),
    lang: string = ''): Observable<any> {
    return service.get(
      path,
      makerOption(params, lang),
    ).pipe(
      catchError((error) => this.handleErrors(error)),
    );
  }

  private reinitialiseSubjectsByKeyword(keywords: string[]): void {
    if (keywords == null) {
      return;
    }
    Object.keys(this.subject).forEach(key => {
      keywords.forEach(keyword => {
        if (key.includes(keyword)) {
          this.subject[key] = null;
        }
      });
    });
  }

  /**
   * This handles errors on requests.
   * Should there be an error, it displays a message to the user and throws an error
   *
   * @param error Request error
   */
  private handleErrors(error: any): Observable<any> {
    if (error.status >= 500 || error.status === 0) {
      return this.throwErrorAndMessage(error, 'Server error');
    }

    if (error.status >= 400 && error.status < 500 && error.status !== 401) {
      return this.throwErrorAndMessage(error, 'Client error');
    }

    if (error.status === 401) {
      if (!this.internalSignUpNeeded(error)) {
        this.router.navigate(['auth/login']);
      }

      return this.throwErrorAndMessage(error, 'Permission error');
    }

    return of(error);
  }

  private makeHttpOptions(params: HttpParams = null, lang: string = ''): any {

    const header = {headers : {}};
    if (environment.key !== '') {
      header['headers']['x-api-key'] = environment.key;
    }

    if (lang) {
      header['headers']['Accept-Language'] = lang;
    }

    return {
      ...(params && {params}),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ...header,
    };
  }

  /**
   * This displays an error to the user and throws it
   *
   * @param error Request error
   * @param title Title of the error to display
   */
  private throwErrorAndMessage(error, title) {
    this.toaster.showServerErrorToast(error.error.detail || error.message || error.error.message, title);
    return throwError(error);
  }
}
